/**
 * Hoa hồng CTV — LŨY TIẾN THEO NGÀY TẠO ĐƠN.
 *
 *  · Chỉ đơn của các shop CTV giới thiệu (sellerType "shop") được đếm và có hoa hồng; đơn
 *    CTV tự bán không có hoa hồng.
 *  · Trong mỗi ngày (giờ Việt Nam, theo ngày TẠO đơn), đơn được xếp thứ tự theo giờ tạo;
 *    đơn nháp và đơn hủy trước khi lấy hàng không chiếm chỗ, đơn hoàn vẫn chiếm chỗ.
 *  · Đơn thứ k trong ngày ăn mức của bậc chứa k: bậc 1–150 giá A, 151–200 giá B → 200 đơn
 *    = 150×A + 50×B.
 *  · Ước tính hiện ngay khi tạo đơn; tiền ghi khi BEST lấy hàng (giao, hoàn hay thất lạc
 *    đều giữ), cộng dồn và chốt theo tháng lấy hàng.
 * Trần (D25): mức cao nhất ≤ chênh lệch nhỏ nhất của bên trả trên CTV đó.
 */

/** Chỉ còn lũy tiến (chế độ "bậc đạt được" đã bỏ). */
export type CommissionMode = 'progressive'

export interface CommissionTier {
  fromOrders: number
  /** null = không giới hạn (bậc cuối). */
  toOrders: number | null
  amountPerOrder: number
}

export interface CommissionSchemeDef {
  mode: CommissionMode
  tiers: CommissionTier[]
}

/** Bậc bắt đầu từ 1, liền nhau, không chồng, bậc cuối mở; mức là số nguyên ≥ 0, không giảm. */
export function validateScheme(def: CommissionSchemeDef): string[] {
  const errors: string[] = []
  const t = def.tiers
  if (t.length === 0) return ['Bảng chiết khấu phải có ít nhất một bậc']
  if (def.mode !== 'progressive') errors.push('Bảng chiết khấu chỉ dùng chế độ lũy tiến')
  if (t[0]!.fromOrders !== 1) errors.push('Bậc đầu phải bắt đầu từ đơn thứ 1')
  t.forEach((tier, i) => {
    const n = i + 1
    if (!Number.isSafeInteger(tier.amountPerOrder) || tier.amountPerOrder < 0) {
      errors.push(`Bậc ${n}: chiết khấu mỗi đơn phải là số nguyên ≥ 0`)
    }
    const isLast = i === t.length - 1
    if (isLast && tier.toOrders !== null) errors.push(`Bậc cuối phải không giới hạn ("từ ${tier.fromOrders}")`)
    if (!isLast) {
      if (tier.toOrders === null) errors.push(`Bậc ${n}: chỉ bậc cuối được không giới hạn`)
      else if (tier.toOrders < tier.fromOrders) errors.push(`Bậc ${n}: "đến" nhỏ hơn "từ"`)
      else if (t[i + 1]!.fromOrders !== tier.toOrders + 1) errors.push(`Bậc ${n + 1} phải bắt đầu từ ${tier.toOrders + 1}`)
    }
    if (i > 0 && tier.amountPerOrder < t[i - 1]!.amountPerOrder) {
      errors.push(`Bậc ${n}: chiết khấu không được thấp hơn bậc trước`)
    }
  })
  return errors
}

export function assertValidScheme(def: CommissionSchemeDef): void {
  const errors = validateScheme(def)
  if (errors.length > 0) throw new Error(errors.join('; '))
}

export function tierFor(def: CommissionSchemeDef, k: number): CommissionTier {
  if (!Number.isSafeInteger(k) || k < 1) throw new Error(`Số thứ tự đơn không hợp lệ: ${k}`)
  const tier = def.tiers.find((t) => k >= t.fromOrders && (t.toOrders === null || k <= t.toOrders))
  if (!tier) throw new Error(`Không có bậc cho đơn thứ ${k}`)
  return tier
}

/** Mức của đơn thứ k trong ngày. */
export function rateForOrder(def: CommissionSchemeDef, k: number): number {
  return tierFor(def, k).amountPerOrder
}

/** Tổng hoa hồng của `count` đơn trong một ngày = Σ mức của đơn 1..count. */
export function totalForCount(def: CommissionSchemeDef, count: number): number {
  let sum = 0
  for (const t of def.tiers) {
    if (count < t.fromOrders) break
    const to = t.toOrders === null ? count : Math.min(t.toOrders, count)
    sum += (to - t.fromOrders + 1) * t.amountPerOrder
  }
  return sum
}

export interface TierProgress {
  currentRate: number
  /** null khi đã ở bậc cuối. */
  nextRate: number | null
  ordersToNext: number | null
}

/** "Còn 51 đơn nữa lên mức 700đ". */
export function tierProgress(def: CommissionSchemeDef, count: number): TierProgress {
  // Chưa có đơn nào → đang ở bậc 1 (mức đơn đầu tiên sẽ được ghi).
  const current = tierFor(def, Math.max(count, 1))
  const next = def.tiers[def.tiers.indexOf(current) + 1]
  if (!next) return { currentRate: current.amountPerOrder, nextRate: null, ordersToNext: null }
  return { currentRate: current.amountPerOrder, nextRate: next.amountPerOrder, ordersToNext: next.fromOrders - count }
}

export function maxRate(def: CommissionSchemeDef): number {
  return Math.max(...def.tiers.map((t) => t.amountPerOrder))
}

export interface CapCheck {
  ok: boolean
  maxRate: number
  minMargin: number
}

/** D25: mức cao nhất không được vượt chênh lệch nhỏ nhất của bên trả trên CTV. */
export function checkCommissionCap(def: CommissionSchemeDef, minMargin: number): CapCheck {
  const m = maxRate(def)
  return { ok: m <= minMargin, maxRate: m, minMargin }
}
